import React from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import ReactTable from "react-table";
import { ButtonGroup, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Popconfirm } from "antd";

import ReactTableFilterSelect from "components/custom/forms/react-table-filter-select";
import filterCaseInsensitive from "components/custom/forms/react-table-case-insensitive";
import { closeCustomModal, openCustomModal } from "components/custom/modals";

import {
    fetchSuppliersList,
    deleteSupplier,
    clearSuppliersList,
    clearSupplier
} from "api/menu/master-files/suppliers/actions";

const SupplierEditModal = React.lazy(() =>
    import("components/menu/master-files/suppliers/create-edit-modal"));
const ManageUsersModal = React.lazy(() =>
    import("components/menu/master-files/suppliers/manage-users-modal"));

class List extends React.Component {
    constructor(props) {
        super(props);

        this.modalOpen = this.modalOpen.bind(this);
        this.handleManageUsersModal = this.handleManageUsersModal.bind(this);
    }

    componentDidMount () {
        this.props.fetchSuppliersList(true);
    }

    componentWillUnmount () {
        this.props.clearSuppliersList();
        this.props.clearSupplier();
    }

    modalOpen = (id = false) => {
        const modalTitle = id !== false ? "Update" : "Create";

        const modalObj = {
            id: `supplier-${id}`,
            modalSettings: {
                size: "lg"
            },
            header: `${modalTitle} Supplier`,
            children: (
                <React.Suspense fallback={<div className="m-2 text-center font-weight-bold">Loading...</div>}>
                    <SupplierEditModal
                        id={id}
                        onCancel={() => this.props.closeCustomModal(modalObj)}
                    />
                </React.Suspense>
            ),
            handleClose: () => this.props.closeCustomModal(modalObj)
        };
        this.props.openCustomModal(modalObj);
    };

    handleManageUsersModal = (supplierId = false) => {
        const modalObj = {
            modalSettings: {
                size: "lg"
            },
            header: "User Management",
            children: (
                <React.Suspense fallback={<div className="m-2 text-center font-weight-bold">Loading...</div>}>
                    <ManageUsersModal
                        onCancel={() => this.props.closeCustomModal(modalObj)}
                        supplierId={supplierId}
                    />
                </React.Suspense>
            ),
            handleClose: () => this.props.closeCustomModal(modalObj)
        };
        this.props.openCustomModal(modalObj);
    };

    delete = (id, code) => {
        this.props.deleteSupplier(id, code, () => {
            this.props.fetchSuppliersList(true);
        });
    };

    render () {
        const {
            suppliers,
            loading
        } = this.props;

        const classNameFlexCenter = "d-flex justify-content-center align-items-center";
        const columns = [
            {
                Header: "Code",
                accessor: "code",
                id: "code",
            },
            {
                Header: "Name",
                accessor: "name",
                id: "name",
            },
            {
                Header: "Active?",
                id: "isInactive",
                accessor: original => (original.isInactive ? "NO" : "YES"),
                Cell: ({ original }) => (original.isInactive ? "NO" : "YES"),
                className: classNameFlexCenter,
                Filter: ({ filter, onChange }) => (
                    <ReactTableFilterSelect
                        currentValue={filter ? filter.value : ""}
                        onChange={value => onChange(value)}
                        style={{ width: "100%", height: "100%" }}
                        optionsObject={{
                            "": "Show All",
                            YES: "Active",
                            NO: "Inactive"
                        }}
                    />
                )
            },
            {
                Header: "Actions",
                id: "actions",
                className: classNameFlexCenter,
                Cell: ({ original }) => (
                    <ButtonGroup>
                        <Button
                            size="sm"
                            variant="warning"
                            onClick={() => this.handleManageUsersModal(original.id)}
                            title="Manage Users"
                        >
                            <FontAwesomeIcon icon="users"/>
                        </Button>
                        <Button
                            size="sm"
                            variant="primary"
                            onClick={() => this.modalOpen(original.id)}
                            title="Update"
                        >
                            <FontAwesomeIcon icon="pen" />
                        </Button>
                        <Popconfirm
                            title={`Delete "${original.code}"?`}
                            onConfirm={() => this.delete(original.id, original.code)}
                            placement="bottomRight"
                            okText="Yes"
                            cancelText="Cancel"
                        >
                            <Button variant="danger" size="sm" title="Remove">
                                <FontAwesomeIcon icon="times" />
                            </Button>
                        </Popconfirm>
                    </ButtonGroup>
                ),
                filterable: false,
                width: 100
            }
        ];

        return (
            <div className="dashboard">
                <div className="d-flex align-items-center justify-content-between">
                    <h3 className="mb-4">Suppliers</h3>
                    <button
                        className="btn btn-primary"
                        onClick={() => this.handleManageUsersModal(false)}
                    >
                        Manage Users
                    </button>
                </div>

                <ReactTable
                    data={suppliers}
                    headerClassName="table-header"
                    columns={columns}
                    defaultPageSize={20}
                    pageSizeOptions={[10, 20, 50, 100]}
                    resizable
                    className="-striped -highlight"
                    loading={loading}
                    filterable
                    defaultFilterMethod={filterCaseInsensitive}
                    defaultSorted={[
                        {
                            id: "code",
                            desc: false
                        }
                    ]}
                    defaultFiltered={[
                        {
                            id: "isInactive",
                            value: "YES"
                        }
                    ]}
                    getNoDataProps = {() => ({
                        style: {
                            zIndex: 0
                        }
                    })}
                    style={{
                        height: "calc(100vh - 200px)",
                    }}
                />

                <div className="mt-4 text-right">
                    <Button className="ml-2" variant="primary" onClick={() => this.modalOpen()}>
                        Add New Supplier
                    </Button>
                </div>
            </div>
        );
    }
}

List.propTypes = {
    fetchSuppliersList: PropTypes.func.isRequired,
    deleteSupplier: PropTypes.func.isRequired,
    clearSuppliersList: PropTypes.func.isRequired,
    clearSupplier: PropTypes.func.isRequired,

    suppliers: PropTypes.array.isRequired,
    loading: PropTypes.bool.isRequired,
};

const mapStateToProps = state => ({
    loading: state.suppliers.loading,
    suppliers: state.suppliers.suppliers
});

const mapStateToDispatch = dispatch => ({
    fetchSuppliersList: bindActionCreators(fetchSuppliersList, dispatch),
    deleteSupplier: bindActionCreators(deleteSupplier, dispatch),
    clearSuppliersList: bindActionCreators(clearSuppliersList, dispatch),
    clearSupplier: bindActionCreators(clearSupplier, dispatch),

    openCustomModal: bindActionCreators(openCustomModal, dispatch),
    closeCustomModal: bindActionCreators(closeCustomModal, dispatch)
});

export default connect(
    mapStateToProps,
    mapStateToDispatch
)(List);
